import type { TimeOfDayData, TodScheduleData, CoordinationSettingsData, SequenceData, CoordinationData } from "@app/models/optimus-configuration.ts";
import { doDateRangesOverlap } from "../../helpers/archive-date-validation";
import { validateBarrierMaxSum, validateRingBarrierPairs } from "../../coordination-settings/helpers/timingValidation";
import { formatTODText } from "@app/helpers/formatting";

export const TimeOfDayErrors = Object.freeze({
	START_TIME_OVERLAP: "start_time_overlap",
	END_TIME_OVERLAP: "end_time_overlap",
	NO_COORDINATION_SETTINGS: "no_coordination_settings",
	NO_SEQUENCE_SETTINGS: "no_sequence_settings",
	NO_PHASE_SETTINGS: "no_phase_settings",
	DANGLING_COORDINATION_SETTINGS: "dangling_coordination_settings",
	DANGLING_PHASE_SETTINGS: "dangling_phase_settings",
	DANGLING_SEQUENCE_SETTINGS: "dangling_sequence_settings",
});

// Brief, identifying descriptor for a TOD: "<DayPattern> (<Months>) <start> - <end>".
// Falls back to the user-typed name when the TOD has no time data yet.
const todDescriptor = (tod: TimeOfDayData): string => {
	const data = tod?.time_of_day_data;
	if (!data) return tod?.name || "TOD";
	const text = formatTODText({
		days: data.days,
		months: data.months,
		local_start_time: data.local_start_time,
		local_end_time: data.local_end_time,
	}).trim();
	if (tod.name && text) return `"${tod.name}" (${text})`;
	return text || tod?.name || "TOD";
};

const formatError = (errorKey: string, row: TimeOfDayData, peer?: TimeOfDayData, hasDateOverlap?: boolean) => {
	const overlapSuffix = hasDateOverlap ? " (overlapping valid ranges)" : "";
	const rowDesc = todDescriptor(row);
	const peerDesc = peer ? ` with ${todDescriptor(peer)}` : "";
	switch (errorKey) {
		case TimeOfDayErrors.START_TIME_OVERLAP:
			return `${rowDesc} - Start overlaps${peerDesc}${overlapSuffix}`;
		case TimeOfDayErrors.END_TIME_OVERLAP:
			return `${rowDesc} - End overlaps${peerDesc}${overlapSuffix}`;
		case TimeOfDayErrors.NO_COORDINATION_SETTINGS:
			return `${rowDesc} - No coordination settings assigned`;
		case TimeOfDayErrors.NO_PHASE_SETTINGS:
			return `${rowDesc} - No phase settings assigned`;
		case TimeOfDayErrors.NO_SEQUENCE_SETTINGS:
			return `${rowDesc} - No sequence settings assigned`;
		case TimeOfDayErrors.DANGLING_COORDINATION_SETTINGS:
			return `${rowDesc} - Coordination settings no longer exist`;
		case TimeOfDayErrors.DANGLING_PHASE_SETTINGS:
			return `${rowDesc} - Phase settings no longer exist`;
		case TimeOfDayErrors.DANGLING_SEQUENCE_SETTINGS:
			return `${rowDesc} - Sequence settings no longer exist`;
	}
	return "";
};
export const normalizeTime = (t: string) => {
	// Normalize HH:MM or HH:MM:SS to HH:MM:SS
	if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
	return t;
};

export const doTimesOverlap = (start1: string, end1: string, start2: string, end2: string) => {
	return !(end1 <= start2 || start1 >= end2);
};

type Day = keyof TodScheduleData["days"];
type Month = keyof TodScheduleData["months"];

const ALL_MONTH_KEYS: Month[] = [
	"january",
	"february",
	"march",
	"april",
	"may",
	"june",
	"july",
	"august",
	"september",
	"october",
	"november",
	"december",
];

const activeDays = (days?: TodScheduleData["days"]): Day[] =>
	days ? (Object.entries(days) as [Day, boolean][]).filter(([, on]) => on).map(([d]) => d) : [];

const activeMonths = (months?: TodScheduleData["months"]): Month[] => {
	if (!months) return [...ALL_MONTH_KEYS];
	const active = (Object.entries(months) as [Month, boolean][]).filter(([, on]) => on).map(([m]) => m);
	return active.length === 0 ? [...ALL_MONTH_KEYS] : active;
};

const ALL_DOM_KEYS: string[] = Array.from({ length: 31 }, (_, i) => String(i + 1));

const activeDaysOfMonth = (doms?: Record<string, boolean>): string[] => {
	if (!doms) return [...ALL_DOM_KEYS];
	const active = ALL_DOM_KEYS.filter((k) => doms[k]);
	return active.length === 0 ? [...ALL_DOM_KEYS] : active;
};

export const validateTimes = (
	timeOfDayData: TimeOfDayData[],
	availableSequenceExternalIds?: string[],
	availablePhaseExternalIds?: string[],
	coordinationSettings?: CoordinationSettingsData[],
	sequences?: SequenceData[],
) => {
	const errors: Record<string, string> = {};

	// Debug: Log the validation inputs
	
	const addToErrorsObject = (
		key: string,
		value: string,
		row: TimeOfDayData,
		peer?: TimeOfDayData,
		hasDateOverlap?: boolean,
	) => {
		if (!key) return;
		const formattedError = formatError(value, row, peer, hasDateOverlap);
		if (formattedError) {
			errors[key] = formattedError;
		}
	};

	

	for (let i = 0; i < timeOfDayData.length; i++) {
		const a = timeOfDayData[i];
		
		const a_tod_id = a.time_of_day_id;
		const aStart = normalizeTime(a.time_of_day_data?.local_start_time);
		const aEnd = normalizeTime(a.time_of_day_data?.local_end_time);

		// Per-row sanity checks (time formatting and start < end)
		if (!/^\d{2}:\d{2}:\d{2}$/.test(aStart)) {
			const errorKey = `${a_tod_id}-start`;
			addToErrorsObject(errorKey, TimeOfDayErrors.START_TIME_OVERLAP, a);
		}
		if (!/^\d{2}:\d{2}:\d{2}$/.test(aEnd)) {
			const errorKey = `${a_tod_id}-end`;
			addToErrorsObject(errorKey, TimeOfDayErrors.END_TIME_OVERLAP, a);
		}

		if (aStart >= aEnd) {
			const errorKeyStart = `${a_tod_id}-start`;
			const errorKeyEnd = `${a_tod_id}-end`;
			addToErrorsObject(errorKeyStart, TimeOfDayErrors.START_TIME_OVERLAP, a);
			addToErrorsObject(errorKeyEnd, TimeOfDayErrors.END_TIME_OVERLAP, a);
		}

		if (!a.phase_settings_external_id) {
			const errorKey = `${a_tod_id}-phase`;
			addToErrorsObject(errorKey, TimeOfDayErrors.NO_PHASE_SETTINGS, a);
		}
		if (!a.sequence_external_id) {
			const errorKey = `${a_tod_id}-sequence`;
			addToErrorsObject(errorKey, TimeOfDayErrors.NO_SEQUENCE_SETTINGS, a);
		}

		// Check for dangling/missing phase settings external_id
		const phaseIsNullOrMissing = a.phase_settings_external_id === null ||
			a.phase_settings_external_id === undefined ||
			a.phase_settings_external_id === "";
		const phaseIsDangling = a.phase_settings_external_id &&
			availablePhaseExternalIds &&
			!availablePhaseExternalIds.includes(a.phase_settings_external_id);

		if (phaseIsNullOrMissing || phaseIsDangling) {
			const errorKey = `${a_tod_id}-phase`;
			addToErrorsObject(errorKey, TimeOfDayErrors.DANGLING_PHASE_SETTINGS, a);
		}

		// Check for dangling/missing sequence settings external_id
		const sequenceIsNullOrMissing = a.sequence_external_id === null ||
			a.sequence_external_id === undefined ||
			a.sequence_external_id === "";
		const sequenceIsDangling = a.sequence_external_id &&
			availableSequenceExternalIds &&
			!availableSequenceExternalIds.includes(a.sequence_external_id);

		if (sequenceIsNullOrMissing || sequenceIsDangling) {
			const errorKey = `${a_tod_id}-sequence`;
			addToErrorsObject(errorKey, TimeOfDayErrors.DANGLING_SEQUENCE_SETTINGS, a);
		}
	}

	// Pairwise overlap checks (dates + days + time windows)
		for (let i = 0; i < timeOfDayData.length; i++) {
			const a = timeOfDayData[i];

			// Get the key for error reporting
			const a_tod_id = a.time_of_day_id;

			// Get start and end times from the appropriate location
			const aStartTime =
				a.time_of_day_data && a.time_of_day_data.local_start_time
					? a.time_of_day_data.local_start_time.substring(0, 5)
					: null;

			const aEndTime =
				a.time_of_day_data && a.time_of_day_data.local_end_time
					? a.time_of_day_data.local_end_time.substring(0, 5)
					: null;

			if (!aStartTime || !aEndTime) continue;

			if (!/^\d{2}:\d{2}$/.test(aStartTime)) {
				const errorKeyStart = `${a_tod_id}-start`;
				addToErrorsObject(errorKeyStart, TimeOfDayErrors.START_TIME_OVERLAP, a);
			}
			if (!/^\d{2}:\d{2}$/.test(aEndTime)) {
				const errorKeyEnd = `${a_tod_id}-end`;
				addToErrorsObject(errorKeyEnd, TimeOfDayErrors.END_TIME_OVERLAP, a);
			}

			if (aStartTime >= aEndTime) {
				const errorKeyStart = `${a_tod_id}-start`;
				const errorKeyEnd = `${a_tod_id}-end`;
				addToErrorsObject(errorKeyStart, TimeOfDayErrors.START_TIME_OVERLAP, a);
				addToErrorsObject(errorKeyEnd, TimeOfDayErrors.END_TIME_OVERLAP, a);
			}

			for (let j = i + 1; j < timeOfDayData.length; j++) {
				const b = timeOfDayData[j];

				// Short-circuit if date ranges do not overlap
				const dateOverlap = doDateRangesOverlap(
					a.valid_from ?? null,
					a.valid_to ?? null,
					b.valid_from ?? null,
					b.valid_to ?? null,
				);
				if (!dateOverlap) continue;

				// Get the key for error reporting
				const bKey = b.key || b.time_of_day_id;

				// Get start and end times from the appropriate location
				const bStartTime =
					b.time_of_day_data && b.time_of_day_data.local_start_time
						? b.time_of_day_data.local_start_time.substring(0, 5)
						: null;

				const bEndTime =
					b.time_of_day_data && b.time_of_day_data.local_end_time
						? b.time_of_day_data.local_end_time.substring(0, 5)
						: null;

				if (!bStartTime || !bEndTime) continue;

				// Use time_of_day_data.days instead of days
				const aDays = activeDays(a.time_of_day_data?.days);
				const bDays = activeDays(b.time_of_day_data?.days);

				const sharedDays = aDays.filter((day) => bDays.includes(day));

				const aMonths = activeMonths(a.time_of_day_data?.months);
				const bMonths = activeMonths(b.time_of_day_data?.months);
				const sharedMonths = aMonths.filter((month) => bMonths.includes(month));

				const aDoms = activeDaysOfMonth(a.time_of_day_data?.days_of_month);
				const bDoms = activeDaysOfMonth(b.time_of_day_data?.days_of_month);
				const sharedDoms = aDoms.filter((d) => bDoms.includes(d));

				const timesOverlap = doTimesOverlap(aStartTime, aEndTime, bStartTime, bEndTime);

				if (sharedDays.length && sharedMonths.length && sharedDoms.length && timesOverlap) {
					if (aStartTime < bEndTime && aStartTime >= bStartTime) {
						const errorKeyStart = `${a_tod_id}-start`;
						addToErrorsObject(errorKeyStart, TimeOfDayErrors.START_TIME_OVERLAP, a, b, true);
					}
					if (aEndTime > bStartTime && aEndTime <= bEndTime) {
						const errorKeyEnd = `${a_tod_id}-end`;
						addToErrorsObject(errorKeyEnd, TimeOfDayErrors.END_TIME_OVERLAP, a, b, true);
					}
					if (bStartTime < aEndTime && bStartTime >= aStartTime) {
						const errorKeyStart = `${bKey}-start`;
						addToErrorsObject(errorKeyStart, TimeOfDayErrors.START_TIME_OVERLAP, b, a, true);
					}
					if (bEndTime > aStartTime && bEndTime <= aEndTime) {
						const errorKeyEnd = `${bKey}-end`;
						addToErrorsObject(errorKeyEnd, TimeOfDayErrors.END_TIME_OVERLAP, b, a, true);
					}
				}
			}
		}

	// Cross-reference validation: validate splits vs cycle when a TOD has both
	// a coordination setting and a sequence selected
	if (coordinationSettings && sequences) {
		for (const tod of timeOfDayData) {
			if (!tod.coordination_settings_id || !tod.sequence_id) continue;

			const coordSetting = coordinationSettings.find(
				(cs) => cs.coordination_settings_id === tod.coordination_settings_id
			);
			const sequence = sequences.find(
				(seq) => seq.sequence_id === tod.sequence_id
			);

			if (!coordSetting?.coordination_settings_data || !sequence?.sequence_data?.phaseSequence) continue;

			const coordData = coordSetting.coordination_settings_data;
			const combinedPhasePlans: CoordinationData = {
				...coordData,
				phaseSequence: sequence.sequence_data.phaseSequence,
			};

			const todId = tod.time_of_day_id;
			const coordName = coordSetting.coordination_settings_external_id;
			const todErrors: Record<string, string> = {};

			validateBarrierMaxSum({
				coordinationSettingsExternalId: coordName,
				phasePlans: combinedPhasePlans,
				currentErrors: todErrors,
				coordinationSettingsId: todId,
				validFrom: tod.valid_from || undefined,
				validTo: tod.valid_to || undefined,
			});

			validateRingBarrierPairs({
				coordinationSettingsExternalId: coordName,
				phasePlans: combinedPhasePlans,
				currentErrors: todErrors,
				coordinationSettingsId: todId,
				validFrom: tod.valid_from || undefined,
				validTo: tod.valid_to || undefined,
			});

			// Merge errors into the main errors object with TOD-prefixed keys
			for (const [key, message] of Object.entries(todErrors)) {
				errors[`${todId}-${key}`] = `${tod.name} - ${message}`;
			}
		}
	}

	return errors;
}
